// Risk map — draws each analyzed lot's actual POLYGON, colored by its risk
// (green = no risk, amber = medium, red = high / non-compliant). Geometry comes
// from /polygons/geojson; the risk is joined by polygon id from the analytics
// rows (which respect all active filters). Raw Leaflet, satellite by default.
import { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { PolygonRow } from './types';
import { EmptyState } from './components';

const RISK: Record<string, string> = {
    low: '#16a34a', medium: '#d97706', high: '#dc2626', critical: '#991b1b',
    compliant: '#16a34a', pending: '#d97706', non_compliant: '#dc2626',
};
const color = (v?: string | null) => RISK[(v || '').toLowerCase()] || '#94a3b8';

type Feat = { type: string; geometry: unknown; properties: Record<string, unknown> };

export default function RiskMap({ geojson, riskRows }: {
    geojson: { type?: string; features?: unknown[] } | null;
    riskRows: PolygonRow[];
}) {
    const elRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);

    const riskById = useMemo(() => {
        const m = new Map<string, PolygonRow>();
        riskRows.forEach((r) => m.set(String(r.polygon_id), r));
        return m;
    }, [riskRows]);

    // Only the lots that are both analyzed-in-range (riskById) and have geometry.
    const features = useMemo(
        () => ((geojson?.features as Feat[] | undefined) || []).filter(
            (f) => riskById.has(String(f.properties?.id))
        ),
        [geojson, riskById]
    );

    useEffect(() => {
        if (!elRef.current || !features.length) return;
        const map = L.map(elRef.current, { scrollWheelZoom: true });
        mapRef.current = map;

        // Base layers — satellite/aerial first so zones are actually visible.
        const esriSatellite = L.tileLayer(
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
            { attribution: '&copy; Esri, Maxar, Earthstar Geographics', maxZoom: 19 }
        );
        const googleHybrid = L.tileLayer(
            'https://mt{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
            { attribution: '&copy; Google Hybrid', subdomains: ['0', '1', '2', '3'], maxZoom: 20 }
        );
        const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors', maxZoom: 19,
        });
        esriSatellite.addTo(map);
        L.control.layers(
            { 'Satellite (Esri)': esriSatellite, 'Google Hybrid': googleHybrid, 'OpenStreetMap': osm },
            {},
            { position: 'topright' }
        ).addTo(map);

        const tip = (r: PolygonRow) => {
            const label = (r.risk_level || r.compliance_status || '').replace('_', ' ');
            return `<strong>${r.polygon_name}</strong><br/>${(r.deforestation_percentage ?? 0).toFixed(1)}% · ${r.commodity}<br/>${r.country || ''}${label ? ' · ' + label : ''}`;
        };

        const gj = L.geoJSON({ type: 'FeatureCollection', features } as never, {
            style: (feature) => {
                const r = feature ? riskById.get(String((feature.properties as Record<string, unknown>)?.id)) : undefined;
                const c = color(r?.risk_level || r?.compliance_status);
                return { color: c, weight: 2, fillColor: c, fillOpacity: 0.4 };
            },
            onEachFeature: (feature, lyr) => {
                const r = riskById.get(String((feature.properties as Record<string, unknown>)?.id));
                if (r) lyr.bindTooltip(tip(r), { sticky: true });
            },
        }).addTo(map);

        // Centroid dots — constant pixel size, so every lot stays visible even at a
        // continental zoom where the polygons themselves are sub-pixel.
        const dots = L.layerGroup().addTo(map);
        features.forEach((f) => {
            const r = riskById.get(String(f.properties?.id));
            if (!r || r.centroid_lat == null || r.centroid_lon == null) return;
            const c = color(r.risk_level || r.compliance_status);
            L.circleMarker([r.centroid_lat, r.centroid_lon], {
                radius: 5, color: '#ffffff', weight: 1.5, fillColor: c, fillOpacity: 0.9,
            }).bindTooltip(tip(r), { sticky: true }).addTo(dots);
        });

        try {
            map.fitBounds(gj.getBounds(), { padding: [25, 25], maxZoom: 13 });
        } catch {
            map.setView([-1.8312, -78.1834], 6);
        }

        return () => { map.remove(); mapRef.current = null; };
    }, [features, riskById]);

    if (!features.length) return <EmptyState message="No lot geometries to map for this period" />;
    return <div ref={elRef} className="h-[300px] w-full rounded-xl overflow-hidden" />;
}
